/**
 * Sensibilisation — Bibliothèque de scénarios prêts pour les actions
 * de sensibilisation pro et associations.
 *
 * Route : /enseignant/sensibilisation
 *
 * Sprint 1 : bibliothèque de 3 modèles initiaux (entreprise / lycée / club moto).
 * Sprint 2 (à venir) : wizard de création + export PDF.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Megaphone,
  Building2,
  GraduationCap,
  Bike,
  Sparkles,
  PenSquare,
  Clock,
  Users,
  ArrowRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import useSeo from '../hooks/useSeo';

const MODELS = [
  {
    id: 'entreprise',
    icon: Building2,
    accent: '#0891b2',
    title: 'Sensibilisation salariés en entreprise',
    public: 'Salariés tertiaire / industrie · 15-30 personnes',
    duree: '1h30 à 2h',
    teaser:
      "Action ciblée sur les trajets domicile-travail, l'accidentalité 2RM et les EPI obligatoires. Adaptable à un comité d'entreprise, une journée QVT ou un événement RSE.",
    objectifs: [
      "Faire prendre conscience de la sur-représentation des 2RM dans la mortalité (21 %).",
      "Identifier les 4 grandes familles d'accidents et les facteurs aggravants.",
      "S'engager sur 3 bonnes pratiques applicables dès le lendemain.",
    ],
    canevasRef: 'Trame Cours en salle — 7 étapes',
    status: 'modèle',
  },
  {
    id: 'lycee',
    icon: GraduationCap,
    accent: '#cf2031',
    title: 'Sensibilisation lycéens 2RM (15-18 ans)',
    public: 'Élèves AM/A1 récent · classe entière 25-35 personnes',
    duree: '2h (1h théorie + 1h démo plateau)',
    teaser:
      "Action conjointe lycée + auto-école : prise de conscience du risque, EPI obligatoires, vitesse et alcool. Idéal en partenariat avec un service prévention santé.",
    objectifs: [
      "Connaître les EPI obligatoires (casque + gants homologués) et leur coût réel.",
      "Comprendre l'effet de la vitesse au carré sur les distances et l'énergie cinétique.",
      "Identifier 3 comportements à risque chez les jeunes motards et les alternatives.",
    ],
    canevasRef: 'Trame Cours en salle — 7 étapes',
    status: 'modèle',
  },
  {
    id: 'club',
    icon: Bike,
    accent: '#16a34a',
    title: 'Sensibilisation club moto / motards expérimentés',
    public: 'Motards 35-65 ans, permis A confirmé · 10-20 personnes',
    duree: '3h (1h théorie + 2h ateliers pratiques plateau)',
    teaser:
      "Action ciblée sur les 72 % de motards expérimentés tués (campagne 2026). Travail sur la sur-confiance, la lecture de route, le freinage d'urgence et la trajectoire de sécurité.",
    objectifs: [
      "Confronter ses représentations sur sa propre maîtrise (auto-évaluation).",
      "Maîtriser la trajectoire de sécurité Extérieur-Intérieur-Extérieur adaptée à la route française.",
      "Réaliser un freinage d'urgence à 50 km/h en moins de 20 m (référence livret C1-g).",
    ],
    canevasRef: 'Mix Cours en salle + Leçon plateau (C1-g, C2-d)',
    status: 'modèle',
  },
];

function ModelCard({ model }) {
  const Icon = model.icon;
  return (
    <Card
      className="overflow-hidden transition-all hover:shadow-md hover:-translate-y-0.5"
      style={{ borderLeft: `4px solid ${model.accent}` }}
      data-testid={`model-card-${model.id}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-3 mb-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white flex-shrink-0"
            style={{ background: model.accent }}
          >
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-lg font-black tracking-tight leading-tight">
              {model.title}
            </h3>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                <Users className="w-3 h-3" /> {model.public}
              </span>
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                <Clock className="w-3 h-3" /> {model.duree}
              </span>
            </div>
          </div>
          <Badge
            variant="outline"
            className="text-[9px] font-bold uppercase tracking-widest flex-shrink-0"
            style={{ borderColor: `${model.accent}55`, color: model.accent }}
          >
            {model.status}
          </Badge>
        </div>

        <p className="text-xs sm:text-sm text-foreground/80 leading-relaxed mb-3">
          {model.teaser}
        </p>

        <div className="mb-3">
          <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1.5">
            Objectifs pédagogiques
          </div>
          <ul className="space-y-1 text-xs">
            {model.objectifs.map((obj, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className="mt-1 w-1 h-1 rounded-full flex-shrink-0" style={{ background: model.accent }} />
                <span className="leading-snug">{obj}</span>
              </li>
            ))}
          </ul>
        </div>

        <div
          className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded inline-block"
          style={{ background: `${model.accent}1a`, color: model.accent }}
        >
          {model.canevasRef}
        </div>
      </CardContent>
    </Card>
  );
}

export default function Sensibilisation() {
  useSeo({
    title: 'Actions de sensibilisation 2RM · Code 2 Roues',
    description:
      "Bibliothèque de scénarios d'actions de sensibilisation 2-roues motorisés pour entreprises, lycées, clubs moto. Modèles structurés conformes aux trames pédagogiques ECSR.",
    path: '/enseignant/sensibilisation',
  });

  return (
    <div className="space-y-6" data-testid="sensibilisation-page">
      {/* Breadcrumb */}
      <Link
        to="/enseignant"
        className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-muted-foreground hover:text-foreground"
        data-testid="back-to-teacher"
      >
        <ArrowLeft className="w-4 h-4" /> Espace Pro
      </Link>

      {/* HEADER */}
      <header
        className="mc-card p-5 sm:p-7 relative overflow-hidden"
        style={{ borderLeft: '6px solid #cf2031' }}
      >
        <div
          className="absolute -top-16 -right-16 w-72 h-72 rounded-full blur-3xl opacity-25 pointer-events-none"
          style={{ background: '#cf2031' }}
          aria-hidden="true"
        />
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-[#cf2031]/15 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-[#cf2031] mb-3">
            <Megaphone className="w-3 h-3" /> Actions pro & associations
          </div>
          <h1
            className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-[1.05]"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            data-testid="sensib-title"
          >
            Monter une action de sensibilisation
          </h1>
          <p className="text-sm sm:text-base text-foreground/80 mt-3 max-w-3xl leading-relaxed">
            Bibliothèque de <strong>scénarios prêts</strong> pour intervenir auprès d&apos;entreprises,
            de lycées ou de clubs moto. Chaque modèle suit la <strong>trame ECSR officielle</strong>{' '}
            et peut être adapté à ton public. Référence : campagne nationale Sécurité Routière
            (motards expérimentés) + données ONISR 2024.
          </p>
        </div>
      </header>

      {/* GRILLE MODÈLES */}
      <section data-testid="models-grid">
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
          <h2 className="text-lg sm:text-xl font-black tracking-tight">
            Modèles disponibles · {MODELS.length}
          </h2>
          <span className="text-xs text-muted-foreground">
            Cliquez un modèle pour découvrir le canevas complet (à venir).
          </span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {MODELS.map((m) => (
            <ModelCard key={m.id} model={m} />
          ))}
        </div>
      </section>

      {/* CTA — Wizard Sprint 2 */}
      <Card
        className="border-2 border-dashed text-center"
        data-testid="wizard-cta"
        style={{ borderColor: '#cf2031' }}
      >
        <CardContent className="p-6 sm:p-8">
          <PenSquare className="w-8 h-8 sm:w-10 sm:h-10 mx-auto text-[#cf2031] mb-2" />
          <h3
            className="text-base sm:text-lg font-black mb-1"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Bientôt : crée ta propre action de sensibilisation
          </h3>
          <p className="text-xs sm:text-sm text-muted-foreground max-w-md mx-auto mb-4">
            Un wizard guidé pour saisir titre, public, durée, objectifs et supports — avec export
            PDF imprimable + mode présentation pour ta classe ou ton intervention.
          </p>
          <Button asChild className="gap-1.5" style={{ background: '#cf2031' }} data-testid="cta-wizard-from-sensib">
            <Link to="/enseignant/creer-fiche">
              <Sparkles className="w-4 h-4" /> Ouvrir le wizard de création <ArrowRight className="w-3 h-3 ml-1" />
            </Link>
          </Button>
        </CardContent>
      </Card>

      {/* Sources */}
      <div className="text-[11px] text-muted-foreground italic px-1">
        Données mobilisables : ONISR 2024 · Campagne Sécurité Routière 2026 « Motards expérimentés » ·
        Référentiel REMC catégorie A · Trames TP-ECSR.
      </div>
    </div>
  );
}
